import {
    Box,
    Chip,
    FormControl,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { ClipboardCheck, Eye } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import MobileInventoryList from '../../component/inventory/MobileInventoryList';
import { INVENTORY_STATUS } from './inventoryStatus';

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function List() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    const [search, setSearch] = useState('');

    const [inventories, setInventories] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        setLoading(true);
        httpClient.get('/inventories', { params: statusFilter ? { status: statusFilter } : {} })
            .then((response) => setInventories(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                console.error('Error fetching inventories:', error);
                setInventories([]);
                setSnackbarMessage('Erro ao carregar inventários.');
                setOpenSnackbar(true);
            })
            .finally(() => setLoading(false));
    }, [statusFilter]);

    const columns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>#{row.code}</Typography>
            ),
        },
        {
            path: 'description',
            name: 'Descrição',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', color: row.description ? 'text.primary' : 'text.disabled' }}>
                        {row.description || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                        {row.scope === 'partial' ? 'Parcial' : 'Completo'}{row.location_label ? ` · ${row.location_label}` : ''}
                    </Typography>
                </Box>
            ),
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => {
                const status = INVENTORY_STATUS[row.status] || INVENTORY_STATUS.draft;
                return <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700 }} />;
            },
        },
        {
            path: 'items_count',
            name: 'Contados',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.counted_count} / {row.items_count}
                </Typography>
            ),
        },
        {
            path: 'created_by',
            name: 'Responsável',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.created_by || '—'}</Typography>
            ),
        },
        {
            path: 'created_at',
            name: 'Criado em',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatDateTime(row.created_at)}</Typography>
            ),
        },
    ];

    const canView = hasPermission('inventory.view');

    const mobileInventories = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return inventories;
        return inventories.filter((row) => [`#${row.code}`, row.code, row.description, row.location_label, row.created_by]
            .some((value) => String(value ?? '').toLowerCase().includes(term)));
    }, [inventories, search]);

    const actions = canView ? [{
        icon: <Eye size={16} strokeWidth={2} />,
        props: {
            onClick: (row) => navigate(`/inventory/${row.id}`),
            tooltip: 'Abrir',
            style: { cursor: 'pointer' },
        },
    }] : [];

    // Shell mobile: lista do protótipo `inventario/lista.html` (mesmos dados, filtro e permissões).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Inventário de Estoque"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity="error"
            >
                <MobileInventoryList
                    inventories={mobileInventories}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                    canCreate={hasPermission('inventory.create')}
                    onOpen={canView ? (row) => navigate(`/inventory/${row.id}`) : undefined}
                    onNew={() => navigate('/inventory/new')}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Inventário de Estoque"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity="error"
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <ClipboardCheck size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Inventário de Estoque
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Faça a contagem física dos produtos e corrija as divergências com o estoque do sistema.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('inventory.create') && (
                        <NewItemButton label="Novo inventário" onClick={() => navigate('/inventory/new')} />
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                        Status
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            value={statusFilter}
                            displayEmpty
                            onChange={(e) => setStatusFilter(e.target.value)}
                            renderValue={(value) => (value ? INVENTORY_STATUS[value]?.label : 'Todos')}
                            sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {Object.entries(INVENTORY_STATUS).map(([value, status]) => (
                                <MenuItem key={value} value={value}>{status.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <DefaultTable
                    colunms={columns}
                    data={inventories}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={canView ? (row) => navigate(`/inventory/${row.id}`) : undefined}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="inventários"
                    sortableFields={['number', 'created_at']}
                    defaultSort={{ field: 'number', direction: 'desc' }}
                />
            </Box>
        </PageScaffold>
    );
}
